'use no memo';

import type { ApiRequestObject } from '../../lib/flotiq-client/types/api-request';
import type { ContentObjectVersion } from '../../lib/flotiq-client/types/api-response';
import type { HookOptions } from './hook-builders/types';

import { useMemo } from 'react';
import useSessionStorageState from 'use-session-storage-state';

import {
  deleteContentObject,
  deleteContentType,
  deletePlan,
  deleteSpace,
  deleteUser,
  deleteUserRole,
  getAllRolesAssigned,
  getAllSpaces,
  getAllUserRoles,
  getAllUsers,
  getApiKey,
  getConstraints,
  getContentObject,
  getContentObjects,
  getContentObjectVersion,
  getContentObjectVersions,
  getContentType,
  getCtdConstraints,
  getPlan,
  getPlans,
  getPublicPlans,
  getSearchObject,
  getSpace,
  getSpacePlanHistory,
  getSpacesList,
  getUser,
  getUserRole,
  getUserRoles,
  getUsers,
  listContentTypes,
  listUnsplashMedia,
  listWebhooks,
  logsWebhooks,
  patchContentObject,
  patchContentType,
  patchPlan,
  patchSpace,
  patchUser,
  patchUserRole,
  putContentObject,
  putContentType,
  putPlan,
  putSpace,
  putUser,
  putUserRole,
} from '../../lib/flotiq-client';
import {
  getMarketplacePlugins,
  getOnboardingApplications,
  getStarters,
  getTutorial,
} from '../../lib/flotiq-client/external-services-requests';

import { createEntityHook } from './hook-builders/entity-hook-builder';
import { createListingHook } from './hook-builders/listing-hook-builder';

export const useContentTypes = createListingHook(
  listContentTypes,
  'content-types',
);

export const useContentType = createEntityHook(
  getContentType,
  putContentType,
  patchContentType,
  deleteContentType,
  'content-types',
  'contentTypeName',
);

const baseCTOHook = createListingHook(getContentObjects, 'content-objects');

export const useContentObjects = (
  contentTypeName?: string,
  optionalParams: null | object = null,
  options: null | object = null,
) => {
  const completeParams = useMemo(
    () => ({ contentTypeName, ...optionalParams }),
    [contentTypeName, optionalParams],
  );
  return baseCTOHook(completeParams, options);
};

const baseObjectHook = createEntityHook(
  getContentObject,
  putContentObject,
  patchContentObject,
  deleteContentObject,
  'content-objects',
);
export const useContentObject = (
  contentTypeName?: string,
  id?: string,
  options: ApiRequestObject = null,
) => {
  const params = useMemo(() => ({ contentTypeName }), [contentTypeName]);
  return baseObjectHook(id, params, options);
};

const baseObjectVersionsHook = createListingHook(
  getContentObjectVersions,
  'content-objects',
);
export const useContentObjectVersions = (
  contentTypeName?: string,
  id?: string,
  hookParams: ApiRequestObject = null,
  hookOptions: ApiRequestObject = null,
) => {
  const params = useMemo(
    () => ({ contentTypeName, id, ...hookParams }),
    [contentTypeName, hookParams, id],
  );
  return baseObjectVersionsHook(params, hookOptions, 'versions');
};

const baseObjectVersionHook = createEntityHook(
  getContentObjectVersion,
  undefined,
  undefined,
  undefined,
  'content-objects',
);
export const useContentObjectVersion = (
  id: string,
  version: ContentObjectVersion,
  hookParams: ApiRequestObject = null,
  hookOptions: ApiRequestObject = null,
) => {
  const params = useMemo(
    () => ({
      contentTypeName: version.internal.contentType,
      versionNumber: version.version,
      ...hookParams,
    }),
    [hookParams, version],
  );

  return baseObjectVersionHook(id, params, hookOptions, 'versions');
};

export const useUnsplashMedia = createListingHook(
  listUnsplashMedia,
  'unsplash',
);

export const useMediaTags = (params: ApiRequestObject) => {
  return useContentObjects('_tag', params);
};

export const useConstraints = createEntityHook(
  getConstraints,
  undefined,
  undefined,
  undefined,
  'constraints',
  'name',
);

const useCtdConstraints = createEntityHook(
  getCtdConstraints,
  undefined,
  undefined,
  undefined,
  'constraints',
  'name',
);

export const useDefinitionsCount = (
  hookParams: ApiRequestObject,
  hookOptions: ApiRequestObject,
) => {
  return useCtdConstraints('cto-count', hookParams, hookOptions);
};

export const useUsers = createListingHook(getUsers, 'users');

export const baseUserHook = createEntityHook(
  getUser,
  putUser,
  patchUser,
  deleteUser,
  'users',
);
export const useUser = (
  id?: string,
  params?: ApiRequestObject,
  options?: ApiRequestObject,
) => {
  'use no memo';
  const [impersonate] = useSessionStorageState<{ id: string }>(
    'cms.impersonate',
  );
  const hookOptions = useMemo(
    () => ({
      staleTime: Infinity,
      ...options,
    }),
    [options],
  );
  return baseUserHook(impersonate?.id || id, params, hookOptions);
};

export const useAllUsers = createListingHook(getAllUsers, ['users', 'admin']);

export const useAllSpaces = createListingHook(getAllSpaces, [
  'spaces',
  'admin',
]);

export const useSearch = createListingHook(getSearchObject, 'search');

export const baseApiKeysHook = createListingHook(getApiKey, 'api-keys');
export const useApiKeys = (
  params?: ApiRequestObject,
  options?: ApiRequestObject,
) => {
  const hookOptions = useMemo(
    () => ({
      staleTime: Infinity,
      ...options,
    }),
    [options],
  );
  return baseApiKeysHook(params, hookOptions);
};

export const useWebhook = createListingHook(listWebhooks, 'content-objects');

export const useUserRoles = createListingHook(getUserRoles, ['users', 'roles']);

export const useUserRole = createEntityHook(
  getUserRole,
  putUserRole,
  patchUserRole,
  deleteUserRole,
  ['users', 'roles'],
);

export const useAllPlans = createListingHook(getPlans, ['plans', 'admin']);
export const useAllPublicPlans = createListingHook(getPublicPlans, 'plans');

export const usePlan = createEntityHook(
  getPlan,
  putPlan,
  patchPlan,
  deletePlan,
  'plans',
);

export const usePluginsSettings = (
  params: ApiRequestObject,
  options: ApiRequestObject = null,
) => {
  'use no memo';
  const hookOptions = useMemo(
    () => ({
      staleTime: Infinity,
      ...options,
    }),
    [options],
  );
  return useContentObjects('_plugin_settings', params, hookOptions);
};

export const useSpace = createEntityHook(
  getSpace,
  putSpace,
  patchSpace,
  deleteSpace,
  'spaces',
);

export const useSpacesList = createListingHook(getSpacesList, 'spaces');

const baseSpacePlanHistory = createListingHook(getSpacePlanHistory, [
  'spaces',
  'history',
]);
export const useSpacePlanHistory = (
  spaceId?: string,
  optionalParams: ApiRequestObject = null,
  options: HookOptions = null,
) => {
  const completeParams = useMemo(
    () => ({ id: spaceId, ...optionalParams }),
    [spaceId, optionalParams],
  );
  return baseSpacePlanHistory(completeParams, options);
};

export const useStarters = createListingHook(getStarters, 'starters');

export const useMarketplacePlugins = createListingHook(
  getMarketplacePlugins,
  'marketplace-plugins',
);

export const useRawTutorial = createListingHook(getTutorial, 'tutorial');

export const useAllUserRoles = createListingHook(getAllUserRoles, [
  'user',
  'roles',
  'all',
]);

const baseAllRolesAssignedHook = createListingHook(getAllRolesAssigned, [
  'users',
  'roles',
  'assigned',
]);
export const useAllRolesAssigned = (
  userId?: string,
  params: ApiRequestObject = null,
  options: ApiRequestObject = null,
) => {
  const completeParams = useMemo(
    () => ({ id: userId, ...params }),
    [params, userId],
  );
  return baseAllRolesAssignedHook(completeParams, options);
};

export const useWebhookLogs = createListingHook(logsWebhooks, 'webhook-logs');

export const useOnboardingApplications = createListingHook(
  getOnboardingApplications,
  'onboarding-applications',
);
